// sort-imports-ignore
import { IonButton, IonIcon, IonRouterLink, IonText } from '@ionic/react';
import React, { useCallback, useMemo } from 'react';

import { ButtonContainer, Spacer } from '@/components/common';
import { useLanguage } from '@/contexts/LanguageContext';
import { useScheduleFlow } from '@/contexts/ScheduleFlowContext';
import { useToast } from '@/hooks';
import { SCHEDULE_FLOW_STEPS } from '@/utils/constants';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import {
  arrowForwardCircleOutline,
  chevronBack,
  chevronForward,
  informationCircleOutline
} from 'ionicons/icons';
import moment from 'moment';
import Calendar from 'react-calendar';
import { Value } from 'react-calendar/dist/cjs/shared/types';

import 'react-calendar/dist/Calendar.css';
import './SelectDate.css';
import { isClinicName } from '@/utils/helpers/isClinicName';

const SelectDate: React.FC = () => {
  const { t } = useLanguage();
  const {
    dentists,
    selectedDentist,
    selectedDate,
    setSelectedDate,
    availableDates,
    setCurrentStep
  } = useScheduleFlow();

  const isUsingClinicName = useMemo(() => {
    return isClinicName(
      `${selectedDentist?.first_name} ${selectedDentist?.last_name}`
    );
  }, [selectedDentist?.first_name, selectedDentist?.last_name]);

  const displayToast = useToast();
  const copy = {
    subHeader: t('schedule.selectDate.subHeader'),
    header: (
      <>
        {t('schedule.selectDate.header.pick')}{' '}
        <IonText color="primary">
          {t('schedule.selectDate.header.date')}
        </IonText>
      </>
    ),
    buttonText: t('schedule.selectDate.buttonText')
  };

  const isDisabled = useCallback(
    (date: Date) => {
      return !availableDates.some((availableDate) =>
        moment(availableDate).isSame(date, 'day')
      );
    },
    [availableDates]
  );

  const onSelect = (day: Value) => {
    const date = new Date(day as Date);
    if (isDisabled(date)) {
      displayToast({
        message: t('schedule.selectDate.errorMessage'),
        duration: 2400,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    } else {
      setSelectedDate(moment(date).isSame(selectedDate, 'day') ? null : date);
    }
  };

  const tileClassName = ({ date }: { date: Date }) => {
    return isDisabled(date) ? 'disabled' : 'enabled';
  };

  const onNext = () => {
    if (selectedDate) {
      setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_TIME);
    } else {
      displayToast({
        message: t('schedule.selectDate.noDateSelectedError'),
        duration: 2400,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  };

  const renderNote = () => {
    if (dentists.length > 1) {
      return (
        <div className="select-date__note">
          <div className="select-date__note-icon">
            <IonIcon icon={informationCircleOutline} />
          </div>
          <div className="select-date__note-text">
            <IonText>{t('schedule.selectDate.notePrefix')}</IonText>{' '}
            {t('schedule.selectDate.noteText', {
              dentistName: formatDentistName({
                dentist: selectedDentist,
                returnDentistFullName: true
              }),
              type: isUsingClinicName ? 'clinic' : 'dentist'
            })}{' '}
            <IonRouterLink
              onClick={() => setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST)}
            >
              {t('schedule.selectDate.changeLinkText', {
                type: isUsingClinicName ? 'clinic' : 'dentist'
              })}
            </IonRouterLink>
            .
          </div>
        </div>
      );
    }
  };

  return (
    <div className="select-date">
      <Spacer top={32} bottom={32}>
        <h4 className="select-date__sub-header">{copy.subHeader}</h4>
        <h1 className="select-date__header">{copy.header}</h1>
      </Spacer>
      <Calendar
        onChange={onSelect}
        onClickDay={onSelect}
        tileClassName={tileClassName}
        minDate={moment().toDate()}
        maxDetail="month"
        minDetail="month"
        prevLabel={<IonIcon icon={chevronBack} color="primary" />}
        nextLabel={<IonIcon icon={chevronForward} color="primary" />}
        value={selectedDate ? moment(selectedDate).toDate() : null}
        showNeighboringMonth={false}
        calendarType="gregory"
      />
      {renderNote()}
      <Spacer top={102} />
      <ButtonContainer>
        <IonButton
          className="select-date__next-button"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          strong
        >
          <IonIcon slot="start" icon={arrowForwardCircleOutline}></IonIcon>
          {copy.buttonText}
        </IonButton>
      </ButtonContainer>
    </div>
  );
};

export default SelectDate;
